import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { config } from '@uhg-abyss/web/tools/config';
import { storage } from '@uhg-abyss/web/tools/storage';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import { Text } from '@uhg-abyss/web/ui/Text';
import React, { createRef, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import { PortalContext } from '../../context/PortalContext';
import { SearchFilterContext } from '../../context/SearchFilterContext';
import { redirectToGuestSignInSchedulingCallback } from '../../frontends/GuestExperience/Guest.utils';
import { useAnnexEncryptedPayload } from '../../hooks/useArcade';
import { useHsidUuid } from '../../hooks/useHsidUuid';
import { useLagoon } from '../../hooks/useLagoon';
import { usePingFederateEncryptedPayload } from '../../hooks/usePingFederate';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { usePCPStore } from '../../store/usePCPStore';
import { PCPSearchStore } from '../../store/usePCPStore/pcpStore';
import { useTypeaheadStore } from '../../store/useTypeaheadStore';
import { TypeaheadState } from '../../store/useTypeaheadStore/typeaheadStore';
import {
  getFormattedAndValidatedPhoneNumber,
  getFormattedPhoneNumber,
} from '../../utils/phone.utils';
import { portalShouldShowOnlineScheduling } from '../../utils/portalConfig.utils';
import {
  getDocAsapLagoonKeys,
  getLagoonConfigValue,
} from '../../utils/providerSearch.utils';
import { getDocAsapTargetUrl } from '../../utils/scheduling.utils';
import { getCurrentMember, getLoggedInMember } from '../../utils/user.utils';
import { AdobeConstantsAscensionSchedule } from '../AdobeTagging/adobeConstants';
import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { Constants, FilterCollectionModel } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { mobileOnly, tabletOnly } from '../ConstantsStyles';
import { getFeatureFlag } from '../Utils/getFeatureFlag';
import { GuestScheduleModal } from './GuestScheduleModal';
import { HiddenFormSubmission } from './ScheduleModal.style';
import { ScheduleModalHiddenForm } from './ScheduleModalHiddenForm';

type Props = {
  ascensionSchedulingLink?: string;
  ascensionIndicator?: boolean;
  isOpen?: boolean;
  npi: string;
  phone: string;
  providerName: string;
  practiceId: string;
  providerId: string;
  locationId: string;
  sectionType: string;
  showDirection: any;
  setIsOpen: (a: boolean) => void;
  isTieredDataCard: boolean;
};
export const AscensionWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

export const AscensionscheduleWrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
});

const ModalSection = styled('div', {
  textAlign: 'center',
  lineHeight: '18px',
});

const ModalButtonSection = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  paddingTop: 'var(--padding-top)',
  paddingBottom: 'var(--padding-bottom)',
});

const ModalWrap = styled(ModalDialog, {
  '.abyss-modal-header-container': { padding: '0px' },
  '.abyss-modal-base-content-container': {
    borderRadius: '8px',
  },
  '.abyss-modal-content-title': {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '52px',
    paddingTop: '14px',
    paddingBottom: '14px',
    lineHeight: '0px',
    '@media (min-width: 985px)': {
      marginTop: '32px',
    },
  },
  '.abyss-modal-close-button': {
    right: '16px',
    top: '16px',
    padding: '0px',
    minWidth: '20px',
    minHeight: '20px',
    '@media (min-width: 985px)': {
      minWidth: '24px',
      minHeight: '24px',
    },
    svg: {
      width: '20px',
      height: '20px',

      color: '#1F2937',
      fill: '#1F2937',
      '@media (min-width: 985px)': {
        width: '24px',
        height: '24px',
      },
    },
  },
  '.abyss-modal-base-overlay': {
    background: 'rgba(0, 0, 0, 0.7)',
  },
});

export const ScheduleModal = ({
  ascensionSchedulingLink,
  ascensionIndicator,
  isOpen,
  npi,
  phone,
  providerName,
  practiceId,
  locationId,
  setIsOpen,
  providerId,
  sectionType,
  showDirection,
  isTieredDataCard,
}: Props) => {
  const currentMember = getCurrentMember();
  const loggedInMember = getLoggedInMember();
  const { t } = useTranslation();
  const hiddenSubmit = createRef<HTMLButtonElement>();
  const [memberSchedulingHeader, setmemberSchedulingHeader] =
    useState<string>('');
  const [memberSchedulingOnline, setmemberSchedulingOnline] =
    useState<string>('');
  const [encryptedPayload, setEncryptedPayload] = useState<string>('');
  const mobileScreen = useMediaQuery(mobileOnly);
  const isTab = useMediaQuery(tabletOnly);
  const checkMobileTab = (mobileScreen, isTab) => mobileScreen || isTab;
  const isSmallScreen = checkMobileTab(mobileScreen, isTab);
  const screenElementSizes = (isSmallScreen) => {
    if (isSmallScreen) {
      return {
        buttonWidth: '100%',
        iconSize: 16,
        size: '320px',
        paddingTop: '12px',
        paddingBottom: '16px',
      };
    }
    return {
      buttonWidth: '226px',
      iconSize: 24,
      size: '624px',
      paddingTop: '0px',
      paddingBottom: '24px',
    };
  };
  const { buttonWidth, size, iconSize, paddingBottom, paddingTop } =
    screenElementSizes(isSmallScreen);
  const [memberId] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MEMBER_ID,
    0
  );
  const featureFlags = useLagoon('feature-flags')();
  const guestScheduleSignInModal = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.GUEST_SCHEDULE_SIGN_IN_MODAL
  );
  const schedulingEnabled = getFeatureFlag(featureFlags, 'ONLINE_SCHEDULING');
  const schedulingPhoneNumber = getFeatureFlag(
    featureFlags,
    'SHOW_SCHEDULING_PHONENUMBER'
  );
  const callPingFederate = getFeatureFlag(featureFlags, 'PING_FEDERATE');
  const ascensionAnalyticsEnabled = React.useMemo(
    () =>
      getFeatureFlag(
        featureFlags,
        ConstantsLagoon.FEATURE_FLAGS.ENABLE_ASCENSION_ANALYTICS
      ),
    [featureFlags]
  );
  const isAscensionAnalyticsEnabled =
    ascensionIndicator && ascensionAnalyticsEnabled;
  const { portalData } = useContext(PortalContext);
  const { searchFilters } = useContext(SearchFilterContext);
  const portalName = portalData?.portalName;

  // Determine if BCC filter is selected
  const isBccFilterSelected =
    searchFilters[FilterCollectionModel.PRIORITY_SCHEDULING]?.value === true;

  const docAsapPartnerCodeKey = getDocAsapLagoonKeys(
    'DOCASAP_PARTNER_CODE',
    portalName,
    isBccFilterSelected
  );
  const docAsapChannelIdKey = getDocAsapLagoonKeys(
    'DOCASAP_CHANNEL_ID',
    portalName,
    isBccFilterSelected
  );

  const allConfigs = useLagoon('config');
  const uiMessaging = useLagoon('ui-messaging')();
  const docAsapUrl = getLagoonConfigValue(allConfigs, 'DOCASAP_URL');
  const docAsapPartnerCode = getLagoonConfigValue(
    allConfigs,
    docAsapPartnerCodeKey
  );
  const docAsapCredentialCode = getLagoonConfigValue(
    allConfigs,
    'DOCASAP_CREDENTIAL_CODE'
  );
  const docAsapChannelId = getLagoonConfigValue(
    allConfigs,
    docAsapChannelIdKey
  );
  const vendorId = getLagoonConfigValue(allConfigs, 'DOCASAP_VENDOR_ID');
  const vsid = getLagoonConfigValue(allConfigs, 'PFID_VS_ID');
  const ssoOutUrl = callPingFederate
    ? getLagoonConfigValue(allConfigs, 'PF_SSOOUT_URL')
    : getLagoonConfigValue(allConfigs, 'FPC_SSOOUT_URL');
  const partnerSpId = getLagoonConfigValue(allConfigs, 'PARTNER_SP_ID');
  const pfidAdapterId = getLagoonConfigValue(allConfigs, 'PFID_ADAPTER_ID');
  const [, getPayload] = useAnnexEncryptedPayload({
    onCompleted: (result) => {
      setEncryptedPayload(result?.data?.annexEncryptedPayload?.payload || '');
    },
  });
  const hsidUuid = useHsidUuid();
  const [, getPingFederatePayload] = usePingFederateEncryptedPayload({
    onCompleted: (result) => {
      setEncryptedPayload(result?.data?.pingFederate?.ref || '');
    },
  });

  const scheduleModelDescription = schedulingPhoneNumber
    ? `${providerName}. ${t('SCHEDULE_MODAL.DOCASAP_CONTENT')}`
    : t('SCHEDULE_MODAL.DESCRIPTION');

  const scheduleModelPhoneNumber =
    schedulingPhoneNumber && phone
      ? `${t('SCHEDULE_MODAL.CALL')} ${
          getFormattedPhoneNumber(phone) as string
        }`
      : `${t('SCHEDULE_MODAL.CALL')}`;

  const CALENDAR_SVG = `${config(
    'CDN_BASE_URL'
  )}/cdn/assets/images/calendar.svg`;

  const CHAT_SVG = `${config(
    'CDN_BASE_URL'
  )}/cdn/assets/images/chat_connection.svg`;

  const schedulingPhoneNumberIcon = schedulingPhoneNumber ? (
    <IconBrand
      data-testid="handshake-icon"
      icon="handshake"
      size="84px"
      variant="twotonelightcircle"
    />
  ) : (
    <img alt="chat" src={CHAT_SVG} />
  );

  const scheduleModalBtnText = portalData?.portalConfig?.isGuestUser
    ? t('SCHEDULE_MODAL.SCHEDULE_SIGN_IN')
    : t('SCHEDULE_MODAL.SCHEDULE');
  const displayIcon = portalData?.portalConfig?.isGuestUser ? (
    ''
  ) : (
    <IconSymbol
      css={{ marginLeft: '8px', color: '#fff' }}
      icon="launch"
      size={iconSize}
    />
  );

  useEffect(() => {
    if (encryptedPayload) {
      hiddenSubmit.current?.click();
    }
  }, [encryptedPayload]);

  const ssoOutTargetUrl = getDocAsapTargetUrl(
    docAsapUrl,
    docAsapPartnerCode,
    docAsapCredentialCode,
    docAsapChannelId,
    npi,
    practiceId
  );
  const showOnlineScheduling = portalShouldShowOnlineScheduling();
  const doShowDocASAP =
    showOnlineScheduling &&
    schedulingEnabled &&
    practiceId &&
    !ascensionIndicator;
  const isGuestUser = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.IS_GUEST
  );
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const choosePCPFlagState = usePCPStore(
    useShallow((state: PCPSearchStore) => ({
      choosePCP: state.pcpSearchState.choosePCP,
    }))
  );

  let choosePCPFlag;

  if (isGuestUser) {
    choosePCPFlag = choosePCPFlagState.choosePCP;
  }

  const dynamicName = isGuestUser
    ? Constants.DETAILS_CHIP_NAMES.SignIn
    : isAscensionAnalyticsEnabled
    ? AdobeConstantsAscensionSchedule.schedule_online
    : Constants.DETAILS_CHIP_NAMES.Schedule;
  const handleScheduleClick = () => {
    adobeLinkTrackEvent({
      name: dynamicName,
      location: isAscensionAnalyticsEnabled
        ? AdobeConstantsAscensionSchedule.location
        : 'modal:schedule a visit:schedule',
      destinationUrl: isGuestUser ? window.location.href : ssoOutTargetUrl,
    });
    if (ascensionIndicator) {
      window.open(ascensionSchedulingLink, '_blank');
      return;
    }
    if (isGuestUser) {
      redirectToGuestSignInSchedulingCallback(
        portalData?.portalConfig?.basePath,
        portalData?.portalConfig?.signInUrl || '',
        chipStore,
        providerId,
        locationId,
        showDirection,
        isTieredDataCard,
        sectionType,
        choosePCPFlag,
        portalData?.portalConfig?.language
      );
    }
    //Added feature flag check to call the correct hook
    if (!isGuestUser) {
      callPingFederate
        ? getPingFederatePayload({
            variables: {
              pingFederatePayload: {
                subject: loggedInMember?.rallyId,
                firstName: currentMember?.demographics?.name.firstName,
                lastName: currentMember?.demographics?.name.lastName,
                dob: currentMember?.demographics?.dateOfBirth,
                groupNumber: currentMember?.policyNumber,
                memberId: memberId.toString(),
                gender: currentMember?.demographics?.gender,
                state: currentMember?.demographics?.address?.[0]?.state,
                zipCode: currentMember?.demographics?.address?.[0]?.zipCode,
                city: currentMember?.demographics?.address?.[0]?.city,
                address1: currentMember?.demographics?.address?.[0]?.address1,
              },
              ...(hsidUuid && { userId: hsidUuid }), // Pass hsidUuid as userId if available
            },
          })
        : getPayload();
    }
  };
  const typeaheadState = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      initiateDocasapSession: state.typeaheadSearchStore.initiateDocasapSession,
    }))
  );
  const handleCallClick = () => {
    const href = `tel:${getFormattedAndValidatedPhoneNumber(phone)}`;
    adobeLinkTrackEvent({
      name: isAscensionAnalyticsEnabled
        ? AdobeConstantsAscensionSchedule.call
        : Constants.DETAILS_CHIP_NAMES.Call,
      location: isAscensionAnalyticsEnabled
        ? AdobeConstantsAscensionSchedule.location
        : 'modal:schedule a visit:call',
      destinationUrl: href,
    });
    window.location.href = href;
  };
  const handleOnCloseClick = () => {
    if (isAscensionAnalyticsEnabled) {
      adobeLinkTrackEvent({
        name: AdobeConstantsAscensionSchedule.close,
        location: AdobeConstantsAscensionSchedule.location,
      });
    }
    setIsOpen(false);
  };
  useEffect(() => {
    if (typeaheadState?.initiateDocasapSession) {
      handleScheduleClick();
    }
  }, [typeaheadState?.initiateDocasapSession]);

  useEffect(() => {
    if (uiMessaging) {
      const MEMBER_SCHEDULING_MODAL_HEADER = uiMessaging.find(
        (x) => x.key === 'MEMBER_SCHEDULING_MODAL_HEADER'
      );
      setmemberSchedulingHeader(MEMBER_SCHEDULING_MODAL_HEADER?.message);
    }
  }, [uiMessaging]);

  useEffect(() => {
    if (uiMessaging) {
      const MEMBER_SCHEDULING_MODAL_ONLINE = uiMessaging.find(
        (x) => x.key === 'MEMBER_SCHEDULING_MODAL_ONLINE'
      );
      setmemberSchedulingOnline(MEMBER_SCHEDULING_MODAL_ONLINE?.message);
    }
  }, [uiMessaging]);

  if (isGuestUser && guestScheduleSignInModal) {
    return (
      <GuestScheduleModal
        isOpen={isOpen ?? false}
        onSignIn={handleScheduleClick}
        setIsOpen={setIsOpen}
      />
    );
  }

  return (
    <ModalWrap
      closeOnClickOutside={false}
      data-auto-testid="schedule-modal-container"
      data-testid="schedule-modal-container"
      header=""
      isOpen={isOpen}
      onClose={handleOnCloseClick}
      size={size}
    >
      <ModalSection>
        <AscensionWrapper>
          <div style={{ margin: '20px' }}>
            {doShowDocASAP ? (
              <img alt="calendar" src={CALENDAR_SVG} />
            ) : (
              schedulingPhoneNumberIcon
            )}
          </div>
          <Text
            css={{
              '&.abyss-text-root': {
                fontWeight: '700',
                fontSize: '14px',
                color: '$core.color.brand.100',
                '@media (min-width: 985px)': {
                  lineHeight: '20px',
                  fontWeight: '700',
                  fontSize: '16px',
                },
              },
            }}
          >
            {t(memberSchedulingHeader)}
          </Text>
          {ascensionIndicator && <Text> {providerName}</Text>}
        </AscensionWrapper>
        <div>
          {ascensionIndicator ? (
            <div style={{ marginTop: '16px', fontSize: '14px' }}>
              {t('SCHEDULE_MODAL.ASCENSION_DESCRIPTION')}
            </div>
          ) : (
            <Text
              css={{
                '&.abyss-text-root': {
                  fontSize: '14px',
                  '@media (min-width: 985px)': {
                    lineHeight: '16px',
                  },
                },
              }}
              data-testid="schedule-modal-description"
              fontWeight="regular"
              size="14px"
            >
              {doShowDocASAP
                ? t(memberSchedulingOnline)
                : scheduleModelDescription}
            </Text>
          )}
        </div>
      </ModalSection>
      <ModalButtonSection
        data-testid="schedule-modal-container"
        style={
          {
            '--padding-top': paddingTop,
            '--padding-bottom': paddingBottom,
          } as React.CSSProperties
        }
      >
        <AscensionscheduleWrapper>
          {(doShowDocASAP || ascensionIndicator) && (
            <React.Fragment>
              <Button
                css={{
                  width: buttonWidth,
                  borderRadius: '24px',
                  fontSize: '16px',
                }}
                data-auto-testid="schedule-modal-schedule-button"
                data-testid="schedule-modal-schedule-button"
                onClick={handleScheduleClick}
                size="sm"
              >
                {ascensionIndicator
                  ? t('DATA_CARD.SCHEDULE_ONLINE')
                  : scheduleModalBtnText}
                {displayIcon}
              </Button>
              <HiddenFormSubmission
                action={ssoOutUrl}
                data-testid="docasap-form"
                method={callPingFederate ? 'get' : 'post'}
                target="_blank"
              >
                <ScheduleModalHiddenForm
                  {...{
                    callPingFederate,
                    encryptedPayload,
                    partnerSpId,
                    pfidAdapterId,
                    ssoOutTargetUrl,
                    ssoOutUrl,
                    vendorId,
                    vsid,
                  }}
                />
                <button ref={hiddenSubmit} type="submit" />
              </HiddenFormSubmission>
            </React.Fragment>
          )}
          {!doShowDocASAP && (
            <Button
              css={{
                width: buttonWidth,
                borderRadius: '24px',
              }}
              data-auto-testid="schedule-modal-call-button"
              data-testid="schedule-modal-call-button"
              onClick={handleCallClick}
              size="sm"
              variant={ascensionIndicator ? 'outline' : 'filled'}
            >
              {!schedulingPhoneNumber && (
                <IconSymbol
                  css={{ marginRight: '8px' }}
                  icon="phone"
                  size={iconSize}
                />
              )}
              {scheduleModelPhoneNumber}
            </Button>
          )}
        </AscensionscheduleWrapper>
      </ModalButtonSection>
    </ModalWrap>
  );
};
